/**
 * AppRouter component with React Router v7 configuration
 *
 * All routes are wrapped in GlobalLayout for shared navigation.
 */

import React, { Suspense } from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { GlobalLayout } from './GlobalLayout'

// Import page components
import HomePage from '../pages/HomePage'
import LoginPage from '../pages/LoginPage'
import NotFoundPage from '../pages/NotFoundPage'

const SettingsPage = React.lazy(() => import('../pages/SettingsPage'))

// Lazy load heavy components for better performance
const CPUListPage = React.lazy(() => import('../pages/CPUListPage'))
const CPUDetailPage = React.lazy(() => import('../pages/CPUDetailPage'))
const StorageListPage = React.lazy(() => import('../pages/StorageListPage'))
const StorageDetailPage = React.lazy(() => import('../pages/StorageDetailPage'))
const GPUListPage = React.lazy(() => import('../pages/GPUListPage'))
const GPUDetailPage = React.lazy(() => import('../pages/GPUDetailPage'))
const CategoryPage = React.lazy(() => import('../pages/CategoryPage'))
const MotherboardListPage = React.lazy(() => import('../pages/MotherboardListPage'))
const MotherboardDetailPage = React.lazy(() => import('../pages/MotherboardDetailPage'))
const MemoryListPage = React.lazy(() => import('../pages/MemoryListPage'))
const MemoryDetailPage = React.lazy(() => import('../pages/MemoryDetailPage'))
const PSUListPage = React.lazy(() => import('../pages/PSUListPage'))
const PSUDetailPage = React.lazy(() => import('../pages/PSUDetailPage'))
const CaseListPage = React.lazy(() => import('../pages/CaseListPage'))
const CaseDetailPage = React.lazy(() => import('../pages/CaseDetailPage'))
const CoolerListPage = React.lazy(() => import('../pages/CoolerListPage'))
const CoolerDetailPage = React.lazy(() => import('../pages/CoolerDetailPage'))
const CompatibilitySpecification = React.lazy(() => import('../pages/CompatibilitySpecification'))
const FavoritesCompatibilityReport = React.lazy(() => import('../pages/FavoritesCompatibilityReport'))
const FavoritesBuilder = React.lazy(() => import('../pages/FavoritesBuilder'))

// Loading component for Suspense (non-detail pages)
const LoadingFallback: React.FC = () => null

// Loading fallback for detail pages — slides in from the right immediately on click
const DetailLoadingFallback: React.FC = () => (
  <div className="page-enter-from-right" />
)

// Error boundary component for route errors
class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error } {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Route Error:', error, errorInfo)
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Something went wrong</h2>
            <p className="mb-6 text-gray-600">
              {this.state.error?.message ?? 'An unexpected error occurred while loading this page.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Helper to wrap lazy pages with error boundary and suspense
const lazy = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <RouteErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <Component />
    </Suspense>
  </RouteErrorBoundary>
)

// Helper for detail pages:
// - While the JS chunk is loading  → DetailLoadingFallback slides in from the right
// - Once the chunk is ready        → the content div mounts fresh and slides in from the right
// (Suspense keeps the inner div unmounted until the chunk resolves, so the animation
//  fires at the exact moment the content becomes visible — not before.)
const lazyDetail = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <RouteErrorBoundary>
    <Suspense fallback={<DetailLoadingFallback />}>
      <div className="page-enter-from-right">
        <Component />
      </div>
    </Suspense>
  </RouteErrorBoundary>
)

// Create router configuration with GlobalLayout as root
const router = createBrowserRouter([
  {
    element: <GlobalLayout />,
    children: [
      // Home
      { path: '/', element: <RouteErrorBoundary><HomePage /></RouteErrorBoundary> },
      { path: '/login', element: <RouteErrorBoundary><LoginPage /></RouteErrorBoundary> },
      { path: '/settings', element: lazy(SettingsPage) },

      // Component pages
      { path: '/cpus', element: lazy(CPUListPage) },
      { path: '/cpus/:id', element: lazyDetail(CPUDetailPage) },
      { path: '/gpus', element: lazy(GPUListPage) },
      { path: '/gpus/:id', element: lazyDetail(GPUDetailPage) },
      { path: '/memory', element: lazy(MemoryListPage) },
      { path: '/memory/:id', element: lazyDetail(MemoryDetailPage) },
      { path: '/storage', element: lazy(StorageListPage) },
      { path: '/storage/:id', element: lazyDetail(StorageDetailPage) },
      { path: '/psus', element: lazy(PSUListPage) },
      { path: '/psus/:id', element: lazyDetail(PSUDetailPage) },
      { path: '/cases', element: lazy(CaseListPage) },
      { path: '/cases/:id', element: lazyDetail(CaseDetailPage) },
      { path: '/coolers', element: lazy(CoolerListPage) },
      { path: '/coolers/:id', element: lazyDetail(CoolerDetailPage) },
      { path: '/motherboards', element: lazy(MotherboardListPage) },
      { path: '/motherboards/:id', element: lazyDetail(MotherboardDetailPage) },
      { path: '/category/:category', element: lazy(CategoryPage) },
      { path: '/compatibility-checker', element: lazy(CompatibilitySpecification) },
      { path: '/my-check', element: lazy(FavoritesCompatibilityReport) },
      { path: '/my-builder', element: lazy(FavoritesBuilder) },

      // Legacy redirects
      { path: '/mother_boards', element: <Navigate to="/motherboards" replace /> },
      { path: '/mother_boards/:id', element: <Navigate to="/motherboards/:id" replace /> },
      { path: '/compare', element: <Navigate to="/cpus" replace /> },
      { path: '/favorites', element: <Navigate to="/my-check" replace /> },
      { path: '/cpu-compare', element: <Navigate to="/cpus" replace /> },

      // Error routes
      { path: '/404', element: <NotFoundPage /> },
      { path: '/error', element: <NotFoundPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

/**
 * Main router component
 */
const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />
}

export default AppRouter
